import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withRouter } from 'utils/withRouter';
import { Flex, Dropdown } from 'ui';
import { FormattedMessage, injectIntl } from 'react-intl';
import { urlSigns } from 'utils/app-configs';

const SortFlex = styled(Flex)`
  border-left: 1px solid ${p => p.theme.colors.border};
  margin-right: 15px;
  flex-flow: row nowrap;
  font-size: 13px;
  height: 34px;
`;

const Title = styled.p`
  margin-left: 15px;
  padding-top: 8px;
`;

const StyledDropdown = styled(Dropdown)`
  margin-left: 10px;
  flex-grow: 3;
  min-width: 250px;
`;

const SortDropdown = ({
  columnDef,
  intl,
  updateSort,
  activeSort,
  isLoading,
  data,
  defaultSortId,
}) => {
  const transformSortToParam = urlParams =>
    // eslint-disable-next-line
    urlParams && `${urlParams?.split(urlSigns.keyValueSeparator)[0]}_${urlParams?.split(urlSigns.keyValueSeparator)[1] === 'true' ?
      'asc' : 'desc'}`;
  const [sortFilterValue, setSortFilterValue] = useState(transformSortToParam(activeSort));
  const [labelRecord, setLabelRecord] = useState();
  const generateDescription = (
    label,
    renderLabelFn,
    displayField,
    type,
    sortType,
    sortName,
    ascending,
  ) => {
    let description;
    const usedType = sortType || type;
    const record = data?.find(d => d[displayField]);
    if (!labelRecord && record) {
      setLabelRecord(record);
    }
    const columnLabel = sortName || label ||
      (renderLabelFn && renderLabelFn(null, labelRecord)?.replace(/\((.*)\)/, '$1')) ||
      intl.formatMessage({ id: 'worklist.filters.NO_LABEL' });

    switch (usedType) {
      case 'date':
        description = `${columnLabel} (${ascending ?
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_DATE_ASC' }) :
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_DATE_DESC' })
          })`;
        break;
      case 'string':
        description = `${columnLabel} (${ascending ?
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_STRING_ASC' }) :
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_STRING_DESC' })
          })`;
        break;
      case 'number':
        description = `${columnLabel} (${ascending ?
          '0-9' :
          '9-0'})`;
        break;
      case 'sequence':
        description = `${columnLabel} (${ascending ? '1-2-3' : '3-2-1'})`;
        break;
      default:
        description = `${columnLabel} (${ascending ?
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_DEFAULT_ASC' }) :
          intl.formatMessage({ id: 'worklist.filters.SORT_DESCRIPTION_DEFAULT_DESC' })
          })`;
    }
    return description;
  };

  const getSortableColumns = () => {
    if (data && data.length) {
      return columnDef &&
        columnDef
          ?.filter(cd => cd.sortable);
    }
    return columnDef &&
      columnDef
        ?.filter(cd => cd.sortable)
        ?.filter(cd => cd.label);
  };

  const loadFilters = () => {
    if (columnDef && columnDef.length) {
      const sortableColumns = getSortableColumns();
      return sortableColumns.length ?
        sortableColumns
          .concat(sortableColumns)
          ?.sort((a, b) => a.sort - b.sort)
          ?.map((sd, i) => ({
            id: sd.id,
            type: sd.type,
            ascending: i % 2 === 0,
            default: sd.id === defaultSortId && i % 2 === 0,
            sortDescription: generateDescription(
              sd.label,
              sd.renderLabelFn,
              sd.displayField,
              sd.type,
              sd.sortType,
              sd.sortName,
              i % 2 === 0,
            ),
            key: `${sd.id}_${i % 2 === 0 ? 'asc' : 'desc'}`,
          })) :
        [];
    }
    return [];
  };

  const [sortFilters, setSortFilters] = useState(loadFilters());

  useEffect(() => {
    setLabelRecord();
    setSortFilters(loadFilters());
  }, [columnDef]);

  useEffect(() => {
    if (activeSort) {
      setSortFilterValue(transformSortToParam(activeSort));
    } else {
      // eslint-disable-next-line
      setSortFilterValue(undefined);
    }
  }, [activeSort]);

  const handleSortChange = (e) => {
    setSortFilterValue(e);
    // Replace the url so the active filters are present (for back functions)
    // eslint-disable-next-line
    const sortParam = `${e?.split('_')[0]}${urlSigns.keyValueSeparator}${e?.split('_')[1] === 'asc' ? 'true' : 'false'}`;
    updateSort(sortParam);
  };

  const getDropdownValue = () => {
    if (isLoading) {
      return intl.formatMessage({ id: 'worklist.filters.DATA_LOADING' });
    }
    return null;
  };

  const checkIfLabelAvailable = (label) => {
    const sortableColumns = getSortableColumns();
    if (sortableColumns?.length) {
      return sortableColumns?.filter(it => it.id === label?.split('_')[0]).length ?
        label : intl.formatMessage({ id: 'worklist.filters.NO_LABEL' });
    }
    return null;
  };

  return (
    <SortFlex>
      <Title>
        <FormattedMessage id='worklist.filters.SORT_FILTERS' />
        {':'}
      </Title>
      <StyledDropdown
        placeholder={intl.formatMessage({
          id: 'worklist.filters.SORT_PLACEHOLDER',
        })}
        onSelect={handleSortChange}
        values={sortFilters}
        disabled={!columnDef || isLoading || !columnDef?.filter(cd => cd.sortable).length}
        valueLabel='sortDescription'
        valueKey='key'
        loading={isLoading}
        value={getDropdownValue() || checkIfLabelAvailable(sortFilterValue)}
        showSearch={false}
      />
    </SortFlex>
  );
};

SortDropdown.propTypes = {
  columnDef: PropTypes.array,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  data: PropTypes.array,
  activeSort: PropTypes.string,
  updateSort: PropTypes.func,
  isLoading: PropTypes.bool,
  defaultSortId: PropTypes.string,
};

export default withRouter(injectIntl(SortDropdown));
